import { Fragment, useState } from "react"
import { useSearchParams } from "react-router"
import { CLASS_LEVELS } from "@client/lib/class-levels"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import {
  Field,
  FormError,
  FormSectionHead,
  PageHero,
  CustomSelectField,
  SubmitButton,
  SuccessCard,
  TextAreaField,
  usePublicForm,
} from "./components"
import { DatePickerField } from "./date-picker"

const STEPS = [
  { image: "/images/pages/nominate-step-1.png", title: "You nominate", body: "Submit the child's details and parent/guardian contact." },
  { image: "/images/pages/nominate-step-2.png", title: "We verify", body: "A Redempta representative on the ground confirms the family is aware and willing." },
  { image: "/images/pages/nominate-step-3.png", title: "Enrolment", body: "If eligible, we match the child with a sponsor and or partner school." },
]

const DISABILITY_CATEGORIES = ["Mobility", "Visual", "Hearing", "Limb difference", "Other"] as const
const SUPPORT_NEEDS = ["Assistive device", "School / university fees", "Medical / therapy", "Skills training", "Other"] as const
const PROGRAMS = ["Scholarship", "Ability Program"] as const
type Program = (typeof PROGRAMS)[number]

function NominateFlowArrow({ reverse = false }: { reverse?: boolean }) {
  const curve = reverse
    ? "M1 1 C25 13 48 28 71 28 C95 28 118 14 141 1"
    : "M1 27 C25 15 50 0 75 2 C100 4 126 17 150 27"
  const arrowHead = reverse
    ? "M141.344 0.921875L132.567 8.42582L143.454 12.2744L141.344 0.921875Z"
    : "M151.047 28.1109L143.839 19.09L139.63 29.8429L151.047 28.1109Z"

  return (
    <svg
      className="nominate-flow-arrow"
      width={reverse ? 144 : 152}
      height={reverse ? 29 : 30}
      viewBox={reverse ? "0 0 144 29" : "0 0 152 30"}
      fill="none"
      aria-hidden
    >
      <path d={curve} fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="7 7" strokeLinecap="round" />
      <path d={arrowHead} fill="currentColor" />
    </svg>
  )
}

export function NominatePage() {
  const [searchParams] = useSearchParams()
  const [program, setProgram] = useState<Program>(() => searchParams.get("program") === "ability" ? "Ability Program" : "Scholarship")
  const { status, submit, reset } = usePublicForm("/api/public/nominations")
  const [confirmedReset, setConfirmedReset] = useState(0)

  const isAbility = program === "Ability Program"

  if (status === "success") {
    return (
      <section className="bg-cream-50 py-20 lg:py-24">
        <div className="public-container">
          <SuccessCard
            eyebrow="Nomination received"
            title="Thank you for looking out."
            message="Your nomination is in. Every submission is verified with the beneficiary or guardian before any next step — a Redempta representative will be in touch."
            onReset={() => {
              setConfirmedReset((n) => n + 1)
              reset()
            }}
          />
        </div>
      </section>
    )
  }

  return (
    <>
      <PageHero
         eyebrow="NOMINATE / REFER"
        title={<>Know a child who <span className="text-forest-700">deserves a chance?</span></>}
        lede={<>Every nomination is verified with the child&apos;s parents or guardians before any next step. <span className="text-forest-700">This is how we protect the child, the family, and the trust of our sponsors.</span></>}
        eyebrowDot
        className="nominate-hero"
      />

      <section className="nominate-flow-section bg-cream-50 py-6 lg:py-10">
        <div className="public-container">
          <div className="nominate-flow">
            {STEPS.map((step, index) => (
              <Fragment key={step.title}>
                <article className="nominate-flow-step">
                  <div className="nominate-flow-icon">
                    <img src={step.image} alt="" aria-hidden />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
                {index < STEPS.length - 1 && <NominateFlowArrow reverse={index === 1} />}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="nominate-form-section bg-cream-50 pb-8 lg:pb-12">
        <div className="public-container">
          <div className="nominate-form-card">
            <form
                key={confirmedReset}
                onSubmit={(event) => {
                  const form = new FormData(event.currentTarget)
                  const get = (key: string) => String(form.get(key) ?? "")
                  submit(event, isAbility
                    ? {
                        program,
                        nomineeName: get("nomineeName"),
                        dateOfBirth: get("dob"),
                        category: get("category"),
                        supportNeed: get("supportNeed"),
                        condition: get("condition"),
                        state: get("state"),
                        lga: get("lga"),
                        contactName: get("contactName"),
                        contactPhone: get("contactPhone"),
                        nominatorName: get("nominatorName"),
                        nominatorContact: get("nominatorContact"),
                        notes: get("notes"),
                      }
                    : {
                        program,
                        childName: get("childName"),
                        dateOfBirth: get("dob"),
                        currentClass: get("currentClass"),
                        state: get("state"),
                        lga: get("lga"),
                        guardianName: get("guardianName"),
                        guardianPhone: get("guardianPhone"),
                        nominatorName: get("nominatorName"),
                        nominatorContact: get("nominatorContact"),
                        notes: get("notes"),
                      })
                }}
                className="public-form nominate-form"
              >
                <CustomSelectField
                  label="Program"
                  name="program"
                  options={PROGRAMS}
                  value={program}
                  onChange={(value) => setProgram(value as Program)}
                  required
                  disabled={status === "loading"}
                />
                <FormSectionHead>{isAbility ? "Nominee details" : "Child details"}</FormSectionHead>
                <div className="nominate-form-group">
                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                    <Field label={isAbility ? "Nominee's full name" : "Child's full name"} name={isAbility ? "nomineeName" : "childName"} required placeholder="First, middle and last name" disabled={status === "loading"} wrapperClassName="sm:col-span-2" />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                    <DatePickerField label="Date of birth" name="dob" required disabled={status === "loading"} />
                    {isAbility ? (
                       <CustomSelectField label="Disability category" name="category" options={DISABILITY_CATEGORIES} required disabled={status === "loading"} />
                    ) : (
                      <CustomSelectField label="Current class" name="currentClass" options={CLASS_LEVELS} placeholder="Select class" required disabled={status === "loading"} />
                    )}
                  </div>
                  {isAbility && (
                    <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                      <CustomSelectField label="Support most needed" name="supportNeed" options={SUPPORT_NEEDS} required disabled={status === "loading"} />
                    </div>
                  )}
                  {isAbility && (
                    <TextAreaField
                      label="Condition and needs"
                      name="condition"
                      required
                      placeholder="Brief explanation of condition and current support needed..."
                      disabled={status === "loading"}
                    />
                  )}
                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                    <CustomSelectField label="State" name="state" options={NIGERIAN_STATES} placeholder="Select state" required disabled={status === "loading"} />
                    <Field label="Local government area" name="lga" required placeholder="e.g., Obio/Akpor" disabled={status === "loading"} />
                  </div>
                </div>

                <FormSectionHead>{isAbility ? "Contact person" : "Parent or guardian"}</FormSectionHead>
                <div className="nominate-form-group">
                  {isAbility ? (
                    <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                      <Field label="Contact name" name="contactName" required placeholder="First, middle and last name" disabled={status === "loading"} />
                      <Field label="Contact phone" name="contactPhone" required type="tel" placeholder="+234..." disabled={status === "loading"} />
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                      <Field label="Parent / guardian name" name="guardianName" required placeholder="First, middle and last name" disabled={status === "loading"} />
                      <Field label="Parent / guardian phone" name="guardianPhone" required type="tel" placeholder="+234..." disabled={status === "loading"} />
                    </div>
                  )}
                </div>

                <FormSectionHead>About you (the nominator)</FormSectionHead>
                <div className="nominate-form-group nominate-form-group--about">
                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                    <Field label="Your name" name="nominatorName" required placeholder="First, middle and last name" disabled={status === "loading"} />
                    <Field label="Your email or phone" name="nominatorContact" required placeholder="+234... or you@example.com" disabled={status === "loading"} />
                  </div>

                  <TextAreaField
                    label="Anything else we should know?"
                    name="notes"
                    placeholder="Family circumstance, current school, etc..."
                    disabled={status === "loading"}
                  />
                </div>

                <label className="nominate-confirmation flex items-start gap-3 text-sm text-ink/80">
                  <input type="checkbox" required className="mt-1 h-4 w-4 rounded border-ink/20 accent-forest-950" disabled={status === "loading"} />
                  <span>
                    {isAbility
                      ? "I confirm the nominee or their guardian is aware of this referral and open to being contacted by Redempta."
                      : "I confirm the child's parent or guardian is aware of this nomination and open to being contacted by Redempta."}
                  </span>
                </label>

                <div>
                  <SubmitButton loading={status === "loading"} withArrow={false}>
                    {isAbility ? "Submit referral" : "Submit nomination"}
                  </SubmitButton>
                  {status === "error" && <FormError />}
                </div>
            </form>
          </div>
        </div>
      </section>
    </>
  )
}
